import { css } from '@emotion/react'
import { IContentfulCategory } from 'api/contentful/contentful.types'
import {
  getArticleList,
  IArticleListItem,
  mutateArticleList,
  useArticleList,
} from 'api/contentful/getArticleList'
import { useCategoryList } from 'api/contentful/getCategories'
import { ArticleCard } from 'components/articleCard/ArticleCard'
import { ArticlesSearchInput } from 'components/articles/articlesSearchInput/ArticlesSearchInput'
import { BorderDecoration } from 'components/BorderDecoration'
import { Button } from 'components/button/Button'
import { ArticlesGrid } from 'components/Grid'
import { Stack } from 'components/Stack'
import { Title, Title_h1_default } from 'components/Typography'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useRef } from 'react'
import { encodeQuery } from 'utils/fetcher'
import { BREAKPOINTS, pxToRem } from 'utils/styled'

function getQueryValue(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value
}

export function ArticleList(props: {
  initialArticles?: IArticleListItem[]
  initialCategories?: IContentfulCategory[]
  isInside?: boolean
}) {
  const { t } = useTranslation()
  const router = useRouter()
  const search = getQueryValue(router.query.search)
  const category = getQueryValue(router.query.category)
  const searchRef = useRef(search)
  const categoryRef = useRef(category)

  useEffect(() => {
    searchRef.current = search
    categoryRef.current = category
  }, [category, search])

  async function onParamsChange(payload: { search?: string; category?: string }) {
    const nextParams = {
      search: payload.search?.trim() || undefined,
      category: payload.category,
    }

    searchRef.current = nextParams.search
    categoryRef.current = nextParams.category

    router.push(encodeQuery(props.isInside ? '/me/articles' : '/articles', nextParams), undefined, {
      shallow: true,
    })

    await mutateArticleList(
      getArticleList({ search: nextParams.search, categoryId: nextParams.category }),
      { search: nextParams.search, categoryId: nextParams.category }
    )
  }

  async function onSearchSubmit(payload: { search: string }) {
    return onParamsChange({
      search: payload.search,
      category,
    })
  }

  async function onCategoryChange(categoryId: string) {
    return onParamsChange({
      search: searchRef.current,
      category: categoryRef.current !== categoryId ? categoryId : undefined,
    })
  }

  async function onSearchClear() {
    return onParamsChange({
      search: undefined,
      category: categoryRef.current,
    })
  }

  const { data: articles, setSize } = useArticleList(
    { search, categoryId: category },
    !search && !category ? props.initialArticles : undefined
  )

  const allArticles = articles?.flat() || []
  const anyNextArticlesAvailable = Boolean(
    articles?.length && articles[articles.length - 1]?.length
  )

  const { data: categories } = useCategoryList(props.initialCategories)

  return (
    <Fragment>
      <Title variant={Title_h1_default} css={{ marginBottom: '1.5625rem' }}>
        {t(
          allArticles?.length === 0
            ? 'common.articles.found_nothing'
            : 'common.articles.found_articles'
        )}
      </Title>

      <ArticlesSearchInput
        query={search}
        onSearchSubmit={onSearchSubmit}
        onSearchClear={onSearchClear}
        foundArticlesCount={allArticles.length}
        css={css`
          width: 40%;

          @media ${BREAKPOINTS.MD.max} {
            width: 100%;
          }
        `}
      />

      <Stack
        css={css`
          margin-bottom: ${pxToRem(30) - 1}rem;
          flex-wrap: wrap;

          & > * {
            margin-bottom: 1rem;
          }

          @media ${BREAKPOINTS.XS.max} {
            flex-direction: column;
            & > *:not(:last-child) {
              margin-right: 0;
              margin-bottom: 1rem;
            }
          }
        `}
        space="1rem"
        orientation="horizontal"
      >
        {categories?.map((item) => (
          <Button
            type="button"
            variant="secondary"
            key={item.sys.id}
            onClick={() => onCategoryChange(item.sys.id)}
            active={category === item.sys.id}
          >
            {item.name}
          </Button>
        ))}
      </Stack>
      {allArticles?.length === 0 && (
        <Title variant={'h5'} css={{ margin: '5.75rem 0 2rem' }}>
          {t('common.articles.other_articles')}
        </Title>
      )}
      <ArticlesGrid>
        <BorderDecoration top={40} left={40} right={-40} bottom={-40} />
        {(allArticles?.length ? allArticles : props.initialArticles?.slice(0, 3))?.map(
          (article) => (
            <Link
              key={article.sys.id}
              href={encodeQuery(`/articles/${article.slug}`, { inside: props.isInside })}
              passHref
              legacyBehavior
            >
              <ArticleCard
                title={article.title}
                coverImage={article.coverImage?.url}
                content={article.content}
              />
            </Link>
          )
        )}
      </ArticlesGrid>
      {anyNextArticlesAvailable ? (
        <div css={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type="button"
            variant="primary"
            size="large"
            block
            onClick={() => {
              setSize((size) => size + 1)
            }}
            css={{ marginTop: '3rem', width: 'auto' }}
          >
            {t('common.load_more')}
          </Button>
        </div>
      ) : null}
    </Fragment>
  )
}
